import { makeAutoObservable } from "mobx";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { ViewEntityStore } from "../../common/store/types";
import { publisherCollectingSocietyService } from "../services/publisherCollectingSocietyService";
import { publisherCollectingSocietyDisplayId } from "./PublisherCollectingSocietyCommonStore";
import { PublisherCollectingSociety } from "../../../api/graphql";
import { collectiveManagementSocietyDisplayId } from "../../collectiveManagementSociety/store/CollectiveManagementSocietyCommonStore";
import { collectiveManagementSocietyService } from "../../collectiveManagementSociety/services/collectiveManagementSocietyService";
import { InitializeParams } from "../../../store/types";

export class PublisherCollectingSocietyViewStore implements ViewEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  service = publisherCollectingSocietyService;
  publisherCollectingSocietyInstance: PublisherCollectingSociety | null = null;
  cardState: Record<string, boolean> = {};
  savedId: string | null = null;
  cancelled = false;
  isDrawerMode = false;
  isModalMode = false;
  callbackParams: CallbackDataParams | null = null;
  idValue: string | undefined;
  collectingSocietyService = collectiveManagementSocietyService;
  collectingSocietySearchValue: string = "";
  collectingSocietyIdValue: string = "";
  collectingSocietySelectedValue: { displayId: string; id: string } | null =
    null;
  cmsAccountNumberValue: string = "";

  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    makeAutoObservable(this);
  }

  destroy() {
    // this object will be unusable
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  toggleCardState(id: string) {
    this.cardState[id] = !this.cardState[id];
  }

  setCardState(id: string, status: boolean) {
    this.cardState[id] = status;
  }

  getCardState(id: string) {
    if (!(id in this.cardState)) {
      this.cardState[id] = true;
    }
    return !!this.cardState[id];
  }

  *loadPublisherCollectingSocietyById(id: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.publisherCollectingSocietyOne({ id });
    this.publisherCollectingSocietyInstance =
      result.publisherCollectingSocietyOne;
    if (this.publisherCollectingSocietyInstance) {
      this.loadValues(this.publisherCollectingSocietyInstance);
    }
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get displayId(): string {
    if (this.publisherCollectingSocietyInstance) {
      return publisherCollectingSocietyDisplayId(
        this.publisherCollectingSocietyInstance,
        this.langStore
      );
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.PublisherCollectingSocietySingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  setContentTop(_contentTop: number) {}

  *returnedData(params: CallbackDataParams): any {
    if (
      (!params.storeId || params.storeId === this.storeId) &&
      params.returnData &&
      !params.returnData.cancelled &&
      params.returnData.resultId
    ) {
      if (params.callerData?.fieldName === "collectingSociety") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.collectiveManagementSocietyOne({
          id: params.returnData.resultId,
        });
        const collectiveManagementSociety =
          result.collectiveManagementSocietyOne;
        this.collectingSocietySelectedValue = collectiveManagementSociety;
      }
    }
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  get collectingSocietyValue() {
    if (this.collectingSocietySelectedValue) {
      return {
        id: this.collectingSocietySelectedValue.id,
        displayId: collectiveManagementSocietyDisplayId(
          this.collectingSocietySelectedValue,
          this.langStore
        ),
      };
    }
    return null;
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.publisherCollectingSocietyInstance = null;
    this.collectingSocietySearchValue = "";
    this.collectingSocietyIdValue = "";
    this.cmsAccountNumberValue = "";
  }

  close() {
    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
    } else {
      this.rootStore.router.goBack();
    }
  }

  cancelPublisherCollectingSociety() {
    this.cancelled = true;
    this.close();
  }

  get isEditEnabled(): boolean {
    return false;
  }

  editPublisherCollectingSociety() {
    const id = this.publisherCollectingSocietyInstance?.id;
    if (!id) return;

    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      this.rootStore.closeModalView();
    }
    this.rootStore.router.goTo(
      "/publisherCollectingSociety/update/" + id,
      undefined,
      undefined,
      undefined,
      this.breadcrumbLabel
    );
  }

  printPublisherCollectingSociety(handlePrint: () => void): void {
    handlePrint();
  }

  *closePublisherCollectingSociety(): any {
    yield this.close();
  }

  loadValues(instance: PublisherCollectingSociety) {
    this.idValue = instance.id;
    this.collectingSocietySelectedValue = instance.collectingSociety;
    this.cmsAccountNumberValue = instance.cmsAccountNumber;
  }

  getState(): InitializeParams {
    return {
      id: this.idValue,
    };
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const id = params?.id;

      if (id) {
        yield this.loadPublisherCollectingSocietyById(id);
      }

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
